/*
  Paper strips as SVG outlines. A strip is a centreline (an array of points)
  and a width; `ribbon` turns it into a closed path with a scissor cut at each
  end. Everything the landing draws, from the hero strip to the Knot's weave,
  goes through this one function, so every edge on the page is cut the same way.
*/

export type Pt = readonly [number, number];

const round = (n: number) => Math.round(n * 10) / 10;

/** `n` points on a cubic Bézier, evenly spaced in t. */
export function cubic(p0: Pt, p1: Pt, p2: Pt, p3: Pt, n: number): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0 : i / (n - 1);
    const u = 1 - t;
    const a = u * u * u;
    const b = 3 * u * u * t;
    const c = 3 * u * t * t;
    const d = t * t * t;
    out.push([a * p0[0] + b * p1[0] + c * p2[0] + d * p3[0], a * p0[1] + b * p1[1] + c * p2[1] + d * p3[1]]);
  }
  return out;
}

/** Arc length of a polyline. */
export function length(pts: readonly Pt[]): number {
  let total = 0;
  for (let i = 1; i < pts.length; i++) total += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
  return total;
}

/** The same polyline as `n` points spaced evenly by arc length. */
export function resample(pts: readonly Pt[], n: number): Pt[] {
  if (pts.length < 2 || n < 2) return pts.slice(0, Math.max(n, 0)) as Pt[];
  const seg: number[] = [0];
  for (let i = 1; i < pts.length; i++) seg.push(seg[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
  const total = seg[seg.length - 1];
  const out: Pt[] = [];
  let j = 1;
  for (let i = 0; i < n; i++) {
    const at = (total * i) / (n - 1);
    while (j < seg.length - 1 && seg[j] < at) j++;
    const span = seg[j] - seg[j - 1] || 1;
    const t = Math.min(1, Math.max(0, (at - seg[j - 1]) / span));
    out.push([pts[j - 1][0] + (pts[j][0] - pts[j - 1][0]) * t, pts[j - 1][1] + (pts[j][1] - pts[j - 1][1]) * t]);
  }
  return out;
}

/**
 * The stretch of a polyline between two fractions of its length, as `n` points
 * spaced evenly. This is how a strip is fed in or pulled out along its own line:
 * the paper never stretches, it slides.
 */
export function slice(pts: readonly Pt[], from: number, to: number, n = pts.length): Pt[] {
  const a = Math.max(0, Math.min(1, from));
  const b = Math.max(a, Math.min(1, to));
  if (pts.length < 2) return pts.slice() as Pt[];
  const seg: number[] = [0];
  for (let i = 1; i < pts.length; i++) seg.push(seg[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
  const total = seg[seg.length - 1] || 1;
  const at = (s: number): Pt => {
    let j = 1;
    while (j < seg.length - 1 && seg[j] < s) j++;
    const t = Math.min(1, Math.max(0, (s - seg[j - 1]) / (seg[j] - seg[j - 1] || 1)));
    return [pts[j - 1][0] + (pts[j][0] - pts[j - 1][0]) * t, pts[j - 1][1] + (pts[j][1] - pts[j - 1][1]) * t];
  };
  const out: Pt[] = [];
  for (let i = 0; i < n; i++) out.push(at(total * (a + ((b - a) * i) / Math.max(1, n - 1))));
  return out;
}

/**
 * A polyline pulled by a hand: every point near `grab` moves with `by`, falling
 * off smoothly over `reach`, so a strip bends where it is held and lies still
 * further away. Strips tied together bend together, because they are near.
 */
export function deform(pts: readonly Pt[], grab: Pt, by: Pt, reach: number): Pt[] {
  if (!by[0] && !by[1]) return pts as Pt[];
  const r2 = 2 * reach * reach;
  return pts.map(([x, y]) => {
    const w = Math.exp(-((x - grab[0]) ** 2 + (y - grab[1]) ** 2) / r2);
    return [x + by[0] * w, y + by[1] * w] as Pt;
  });
}

/** How far a pull of `d` actually moves the paper: freely at first, then less and less, never past `limit`. */
export const rubber = (d: number, limit: number) => (limit * d) / (limit + Math.abs(d));

/** Joins polylines end to start, dropping the duplicated seam point. */
export function join(...parts: readonly (readonly Pt[])[]): Pt[] {
  const out: Pt[] = [];
  for (const part of parts) {
    for (const p of part) {
      const last = out[out.length - 1];
      if (last && Math.abs(last[0] - p[0]) < 0.01 && Math.abs(last[1] - p[1]) < 0.01) continue;
      out.push(p);
    }
  }
  return out;
}

/** Point-by-point blend of two polylines of the same length. `t` may overshoot 1. */
export function mix(a: readonly Pt[], b: readonly Pt[], t: number): Pt[] {
  return a.map((p, i) => [p[0] + (b[i][0] - p[0]) * t, p[1] + (b[i][1] - p[1]) * t] as Pt);
}

function normals(pts: readonly Pt[]): Pt[] {
  return pts.map((_, i) => {
    const a = pts[Math.max(0, i - 1)];
    const b = pts[Math.min(pts.length - 1, i + 1)];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = Math.hypot(dx, dy) || 1;
    return [-dy / len, dx / len] as Pt;
  });
}

function smooth(side: readonly Pt[]): string {
  let d = "";
  for (let i = 1; i < side.length - 1; i++) {
    const mx = (side[i][0] + side[i + 1][0]) / 2;
    const my = (side[i][1] + side[i + 1][1]) / 2;
    d += `Q${round(side[i][0])} ${round(side[i][1])} ${round(mx)} ${round(my)}`;
  }
  const last = side[side.length - 1];
  return d + `L${round(last[0])} ${round(last[1])}`;
}

export interface Cut {
  /** Slant of the scissor cut at the start, as a fraction of the width. 0 is square. */
  start?: number;
  /** Slant of the cut at the end. */
  end?: number;
}

/**
 * The closed outline of a strip of `width` along `pts`. Each end is a straight
 * scissor cut, slanted by `cut` so it reads as cut by hand rather than drawn.
 * `width` may be one per point, for a strip that narrows along its length.
 */
export function ribbon(pts: readonly Pt[], width: number | readonly number[], cut: Cut = {}): string {
  if (pts.length < 2) return "";
  const w = (i: number) => (typeof width === "number" ? width : width[i]);
  const ns = normals(pts);
  const left = pts.map((p, i) => [p[0] + (ns[i][0] * w(i)) / 2, p[1] + (ns[i][1] * w(i)) / 2] as Pt);
  const right = pts.map((p, i) => [p[0] - (ns[i][0] * w(i)) / 2, p[1] - (ns[i][1] * w(i)) / 2] as Pt);

  const tangent = (i: number, j: number) => {
    const dx = pts[j][0] - pts[i][0];
    const dy = pts[j][1] - pts[i][1];
    const len = Math.hypot(dx, dy) || 1;
    return [dx / len, dy / len] as const;
  };
  const n = pts.length - 1;
  if (cut.start) {
    const [tx, ty] = tangent(0, 1);
    left[0] = [left[0][0] - tx * w(0) * cut.start, left[0][1] - ty * w(0) * cut.start];
  }
  if (cut.end) {
    const [tx, ty] = tangent(n - 1, n);
    right[n] = [right[n][0] + tx * w(n) * cut.end, right[n][1] + ty * w(n) * cut.end];
  }

  const back = right.slice().reverse();
  return `M${round(left[0][0])} ${round(left[0][1])}${smooth(left)}L${round(back[0][0])} ${round(back[0][1])}${smooth(back)}Z`;
}
